import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { ChartPie, Wrench, Warning, WarningOctagon, ClockCounterClockwise, Truck, GasPump, Trophy, User } from "@phosphor-icons/react";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const COLORS = ["#2563eb", "#0ea5e9", "#facc15", "#10b981", "#ef4444", "#8b5cf6"];

export default function Analysis() {
  const [data, setData] = useState(null);
  const [maintByVehicle, setMaintByVehicle] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [vehicleAvg, setVehicleAvg] = useState([]);
  const [driverRanking, setDriverRanking] = useState([]);

  const load = () => {
    api.get("/analysis/summary").then((r) => setData(r.data));
    api.get("/analysis/maintenance-by-vehicle").then((r) => setMaintByVehicle(r.data)).catch(() => {});
    api.get("/vehicles/revision-alerts").then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/analysis/vehicle-avg-consumption").then((r) => setVehicleAvg(r.data)).catch(() => {});
    api.get("/analysis/driver-ranking").then((r) => setDriverRanking(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("live-event", h);
    return () => window.removeEventListener("live-event", h);
  }, []);
  if (!data) return <p className="text-muted-foreground">Carregando análise…</p>;

  const composition = [
    { name: "Comissão", value: data.total_commission },
    { name: "Diesel", value: data.total_diesel },
    { name: "ARLA", value: data.total_arla },
    { name: "Despesas", value: data.total_expenses },
    { name: "Lucro Empresa", value: Math.max(0, data.company_profit) },
  ].filter((x) => x.value > 0);

  const perTrip = data.per_trip.slice(-10).map((t) => ({
    name: `${t.origin || "?"}→${t.destination || "?"}`.slice(0, 20),
    Bruto: t.gross, Líquido: t.net, Comissão: t.commission,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
          <ChartPie size={32} weight="duotone" className="text-blue-600" /> Análise
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Gráficos comparativos: viagens, lucro empresa, comissões</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: "Viagens", v: data.trip_count, s: "" },
          { l: "Faturado bruto", v: fmt(data.total_gross_freight), s: "" },
          { l: "Lucro empresa", v: fmt(data.company_profit), s: "text-emerald-700" },
          { l: "Comissões", v: fmt(data.total_commission), s: "text-yellow-700" },
        ].map((k) => (
          <Card key={k.l}><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{k.l}</div>
            <div className={`text-2xl font-extrabold mt-1 ${k.s}`} style={{ fontFamily: "Manrope" }}>{k.v}</div>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card><CardHeader><CardTitle className="text-lg">Composição de Custos vs Lucro</CardTitle></CardHeader>
          <CardContent style={{ height: 340 }}>
            {composition.length === 0 ? <p className="text-muted-foreground text-sm">Sem dados ainda.</p> : (
              <ResponsiveContainer><PieChart>
                <Pie data={composition} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={(e) => e.name}>
                  {composition.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend />
              </PieChart></ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card><CardHeader><CardTitle className="text-lg">Últimas 10 viagens</CardTitle></CardHeader>
          <CardContent style={{ height: 340 }}>
            {perTrip.length === 0 ? <p className="text-muted-foreground text-sm">Sem viagens.</p> : (
              <ResponsiveContainer><BarChart data={perTrip}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend />
                <Bar dataKey="Bruto" fill="#2563eb" />
                <Bar dataKey="Líquido" fill="#10b981" />
                <Bar dataKey="Comissão" fill="#facc15" />
              </BarChart></ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card><CardHeader><CardTitle className="text-lg">Totais Operacionais</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><div className="text-muted-foreground">Toneladas transportadas</div><div className="text-xl font-bold">{data.total_tons}</div></div>
          <div><div className="text-muted-foreground">KM rodados</div><div className="text-xl font-bold">{data.total_km}</div></div>
          <div><div className="text-muted-foreground">Diesel gasto</div><div className="text-xl font-bold">{fmt(data.total_diesel)}</div></div>
          <div><div className="text-muted-foreground">ARLA gasto</div><div className="text-xl font-bold">{fmt(data.total_arla)}</div></div>
        </CardContent>
      </Card>

      {/* Média km/L por Veículo */}
      {vehicleAvg.length > 0 && (
        <Card data-testid="vehicle-avg-consumption-card" className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <GasPump size={22} weight="duotone" className="text-blue-600" /> Média Geral por Veículo (km/L)
            </CardTitle>
            <p className="text-xs text-slate-600 mt-1">Consolidação de todos os abastecimentos de diesel — mostra qual caminhão está mais econômico.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {vehicleAvg.map((v) => {
              const cfg = v.status === "green"
                ? { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", label: "Ótimo" }
                : v.status === "amber"
                ? { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", label: "Atenção" }
                : { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", label: "Baixo" };
              return (
                <div key={v.vehicle_id} data-testid={`vehicle-avg-row-${v.vehicle_id}`} className={`flex items-center gap-3 border rounded-lg p-3 flex-wrap ${cfg.bg} ${cfg.border}`}>
                  <Truck size={22} weight="fill" className="text-blue-600" />
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-bold text-slate-900">{v.plate_truck} {v.model && <span className="text-slate-500 font-normal">— {v.model}</span>}</div>
                    <div className="text-xs text-slate-600">{v.fueling_count} abastecimento(s) · {v.total_km.toLocaleString("pt-BR")} km · {v.total_liters.toLocaleString("pt-BR")} L</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-extrabold ${cfg.text}`} style={{ fontFamily: "Manrope" }}>{v.avg_km_per_liter.toLocaleString("pt-BR")} km/L</div>
                    <div className={`text-xs font-semibold ${cfg.text}`}>{cfg.label}</div>
                  </div>
                </div>
              );
            })}
            <div className="text-xs text-slate-500 flex gap-3 flex-wrap pt-2">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500" /> Ótimo (&gt; 3 km/L)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500" /> Atenção (2–3 km/L)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500" /> Baixo (&lt; 2 km/L)</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ranking de Motoristas */}
      {driverRanking.length > 0 && (
        <Card data-testid="driver-ranking-card" className="border-2 border-yellow-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy size={22} weight="duotone" className="text-yellow-600" /> Ranking de Motoristas (km/L)
            </CardTitle>
            <p className="text-xs text-slate-600 mt-1">Quem tem a melhor média de consumo. Baseado em todos os abastecimentos de diesel.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {driverRanking.map((d) => {
              const cfg = d.status === "green"
                ? { text: "text-emerald-700" }
                : d.status === "amber"
                ? { text: "text-amber-700" }
                : { text: "text-red-700" };
              const medal = d.rank === 1 ? "🥇" : d.rank === 2 ? "🥈" : d.rank === 3 ? "🥉" : `#${d.rank}`;
              const rankBg = d.rank === 1 ? "bg-yellow-400 text-yellow-900" : d.rank === 2 ? "bg-slate-300 text-slate-800" : d.rank === 3 ? "bg-orange-400 text-orange-900" : "bg-blue-600 text-white";
              return (
                <div key={d.driver_id} data-testid={`driver-rank-row-${d.driver_id}`} className="flex items-center gap-3 border rounded-lg p-3 flex-wrap bg-white">
                  <div className={`w-11 h-11 rounded-full font-extrabold flex items-center justify-center text-xl ${rankBg}`}>{medal}</div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-bold text-slate-900 flex items-center gap-2"><User size={16} weight="fill" className="text-slate-600" /> {d.name}</div>
                    <div className="text-xs text-slate-600">{d.fueling_count} abastecimento(s) · {d.total_km.toLocaleString("pt-BR")} km · {d.total_liters.toLocaleString("pt-BR")} L</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-extrabold ${cfg.text}`} style={{ fontFamily: "Manrope" }}>{d.avg_km_per_liter.toLocaleString("pt-BR")} km/L</div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Alertas de Revisão */}
      {alerts.length > 0 && (
        <Card className="border-2 border-amber-300 bg-amber-50/40" data-testid="revision-alerts-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <ClockCounterClockwise size={22} weight="duotone" /> Alertas de Revisão ({alerts.length})
            </CardTitle>
            <p className="text-xs text-amber-700 mt-1">Veículos que se aproximam do KM da próxima revisão (aviso a partir de 2.000 km)</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.map((a) => {
              const overdue = a.status === "overdue";
              const critical = a.status === "critical";
              const barColor = overdue ? "bg-red-500" : critical ? "bg-red-400" : "bg-amber-400";
              const iconColor = overdue ? "text-red-700" : critical ? "text-red-600" : "text-amber-700";
              const progress = Math.max(0, Math.min(100, ((2000 - a.km_remaining) / 2000) * 100));
              return (
                <div key={a.vehicle_id} data-testid={`revision-alert-${a.vehicle_id}`} className={`bg-white border rounded-lg p-3 ${overdue ? "border-red-400" : critical ? "border-red-300" : "border-amber-300"}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                      {overdue ? <WarningOctagon size={22} weight="fill" className={iconColor} /> : <Warning size={22} weight="fill" className={iconColor} />}
                      <div>
                        <div className="font-bold text-slate-900 flex items-center gap-2"><Truck size={16} weight="fill" className="text-blue-600" /> {a.plate_truck} {a.model && <span className="text-slate-500 font-normal">— {a.model}</span>}</div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          KM atual: <strong>{a.current_km.toLocaleString("pt-BR")}</strong> · Próx. revisão: <strong>{a.next_service_km.toLocaleString("pt-BR")}</strong>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {overdue ? (
                        <div className="text-red-700 font-extrabold text-lg">🚨 ATRASADA</div>
                      ) : (
                        <div className={`font-extrabold text-lg ${critical ? "text-red-600" : "text-amber-700"}`}>{a.km_remaining.toLocaleString("pt-BR")} km</div>
                      )}
                      <div className="text-xs text-slate-500">{overdue ? `${Math.abs(a.km_remaining).toLocaleString("pt-BR")} km passou` : "restantes"}</div>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${barColor} transition-all`} style={{ width: `${progress}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Gastos com Manutenção por Veículo */}
      {maintByVehicle.filter((v) => v.total_cost > 0).length > 0 && (
        <Card data-testid="maintenance-by-vehicle-card">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Wrench size={22} weight="duotone" className="text-blue-600" /> Gastos com Manutenção por Veículo</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div style={{ height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={maintByVehicle.filter((v) => v.total_cost > 0).slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="plate_truck" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend />
                  <Bar dataKey="part_cost" name="Peças" stackId="a" fill="#2563eb" />
                  <Bar dataKey="labor_cost" name="Mão-de-obra" stackId="a" fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid gap-2">
              {maintByVehicle.filter((v) => v.total_cost > 0).slice(0, 5).map((v, idx) => (
                <div key={v.vehicle_id} data-testid={`maint-vehicle-row-${v.vehicle_id}`} className="flex items-center gap-3 border rounded-lg p-3 flex-wrap">
                  <div className={`w-8 h-8 rounded-full font-bold flex items-center justify-center text-white ${idx === 0 ? "bg-red-500" : idx === 1 ? "bg-orange-500" : "bg-blue-600"}`}>{idx + 1}</div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-bold text-slate-900">{v.plate_truck} <span className="text-slate-500 font-normal">— {v.model}</span></div>
                    <div className="text-xs text-slate-600">{v.maint_count} manutenção(ões) · Última: {v.last_service_date || "?"}</div>
                    {v.top_parts?.length > 0 && (
                      <div className="text-xs text-slate-500 mt-1">
                        <span className="font-semibold">Peças + trocadas:</span> {v.top_parts.map((p) => `${p.name} (${p.count}×)`).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Peças: {fmt(v.part_cost)} · MO: {fmt(v.labor_cost)}</div>
                    <div className="text-xl font-extrabold text-red-700" style={{ fontFamily: "Manrope" }}>{fmt(v.total_cost)}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
